import { Field, Formik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { rendaSchema } from '@schemas';
import { useSelector, useDispatch } from 'react-redux';
import { ComboBox, TextInput, Button, CurrencyTextInput } from '@components';
import { LimitePorcentagemSaqueService } from '@services';
import { PLANO_TAESA } from '@utils/plans';
import { useNavigate } from 'react-router-dom';
import { parse } from 'date-fns';
import {
  opcoesContribuicao,
  opcoesRenda,
  opcoesCrescimentoSalario,
  opcoesRetornoRealInvestimento,
  opcoesRetornoRealInvestimentoTaesa,
  opcoesQtdEstimadaRemuneracao,
  opcoesPrazoRendaTemporaria,
  ID_PERCENT_SALDO,
  ID_TEMPORARIO,
  ID_VITALICIO,
  ID_CONJUGADO,
} from './utils';

import {
  opcoesTelaRenda,
  resetaOpcoesTelaRenda,
} from '../../../../store/modules/simulacaoBeneficio/actions';
import { BENEFICIARIOS_ABSOLUTE_PATH } from '../../../../common/utils/paths';

const DATA_DE_CORTE_ADMISSAO_PARTICIPANTE = '2018-05-01';

export const Renda = () => {
  const [opcoesPercSaque, setOpcoesPercSaque] = useState([]);
  const dadosRenda = useSelector((state) => state.simulacaoBeneficio);
  const [opcoesRendaSelect, setOpcoesRendaSelect] = useState(opcoesRenda);
  const user = useSelector((state) => state.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const opcoesRetornoReal =
    user.plano === PLANO_TAESA
      ? opcoesRetornoRealInvestimentoTaesa
      : opcoesRetornoRealInvestimento;

  const atualizarRenda = (rendaAtualizada) => {
    dispatch(opcoesTelaRenda(rendaAtualizada));
    navigate(BENEFICIARIOS_ABSOLUTE_PATH);
  };

  useEffect(() => {
    if (
      parse(user.dadosPessoais.dataAdmissao, 'yyyy-MM-dd', new Date()) >=
        parse(DATA_DE_CORTE_ADMISSAO_PARTICIPANTE, 'yyyy-MM-dd', new Date()) ||
      user.plano === PLANO_TAESA
    ) {
      setOpcoesRendaSelect(
        opcoesRenda.filter((item) => {
          return item.label === 'Percentual Saldo';
        })
      );
    }
  }, [user.dadosPessoais.dataAdmissao, user.plano]);

  const recuperaOpcoesPercSaque = useCallback(async () => {
    const limitePorcentagemSaqueService = new LimitePorcentagemSaqueService();
    const opcoes = [];

    const response =
      await limitePorcentagemSaqueService.getLimitePorcentagemSaque(
        user.matricula
      );
    response.data.percentualSaqueVista.forEach((element) => {
      const item = { value: element.toString(), label: `${element}%` };

      opcoes.push(item);
    });

    setOpcoesPercSaque(opcoes);
  }, []);

  useEffect(() => {
    recuperaOpcoesPercSaque();
  }, [recuperaOpcoesPercSaque]);

  return (
    <div className="card">
      <div className="card-body pb-3">
        <Formik
          initialValues={{
            tipoRendaMensal: dadosRenda.tipoRendaMensal,
            percentualRendaMensal: dadosRenda.percentualRendaMensal,
            mesesAposentar: dadosRenda.mesesAposentar,
            percentualContribuicao: dadosRenda.percentualContribuicao,
            remuneracaoMensal: dadosRenda.remuneracaoMensal,
            crescimentoSalario: dadosRenda.crescimentoSalario,
            remuneracoesAno: dadosRenda.remuneracoesAno,
            retornoInvestimento: dadosRenda.retornoInvestimento,
            percentualSaque: dadosRenda.percentualSaque,
            prazoRendaTemporaria: dadosRenda.prazoRendaTemporaria,
            percentualrcm: dadosRenda.percentualrcm,
            percentualSaldoVitalicio: dadosRenda.percentualSaldoVitalicio,
          }}
          validationSchema={rendaSchema(user.plano === PLANO_TAESA)}
          onSubmit={(values, { setSubmitting }) => {
            const newValues = {
              ...values,
              percentualrcm:
                values.percentualrcm === '' ? 0 : values.percentualrcm,
              percentualRendaMensal:
                values.percentualRendaMensal === ''
                  ? 0
                  : values.percentualRendaMensal,
              mesesAposentar:
                values.mesesAposentar === '' ? 0 : values.mesesAposentar,
              percentualSaldoVitalicio:
                values.percentualSaldoVitalicio === ''
                  ? 0
                  : values.percentualSaldoVitalicio,
            };
            setSubmitting(false);
            atualizarRenda(newValues);
          }}
        >
          {({ handleSubmit, isValid, values, resetForm, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-12 col-lg-6">
                  <Field
                    className="mb-3"
                    name="tipoRendaMensal"
                    component={ComboBox}
                    label="Tipo de benefício mensal que deseja receber:"
                    onChange={(e) => {
                      const tipoRendaSelecionado = e;
                      resetForm();
                      setFieldValue(
                        'tipoRendaMensal',
                        tipoRendaSelecionado.value
                      );
                    }}
                    options={opcoesRendaSelect.map((x) => {
                      return { value: x.value, label: x.label };
                    })}
                  />
                  {values.tipoRendaMensal === ID_CONJUGADO && (
                    <Field
                      className="mb-3"
                      name="percentualSaldoVitalicio"
                      component={TextInput}
                      type="number"
                      label="Percentual do saldo destinado à parcela vitalícia (50% a 99%):"
                    />
                  )}
                  {(values.tipoRendaMensal === ID_PERCENT_SALDO ||
                    values.tipoRendaMensal === ID_CONJUGADO) && (
                    <Field
                      className="mb-3"
                      name="percentualRendaMensal"
                      component={TextInput}
                      type="number"
                      label="Percentual de retirada mensal (0,10% a 1,00%):"
                    />
                  )}
                  {values.tipoRendaMensal === ID_TEMPORARIO && (
                    <Field
                      className="mb-3"
                      name="prazoRendaTemporaria"
                      component={ComboBox}
                      label="Duração da renda temporária:"
                      options={opcoesPrazoRendaTemporaria.map((x) => {
                        return { value: x.value, label: x.label };
                      })}
                    />
                  )}
                  {(values.tipoRendaMensal === ID_VITALICIO ||
                    values.tipoRendaMensal === ID_CONJUGADO) && (
                    <Field
                      className="mb-3"
                      name="percentualrcm"
                      type="number"
                      component={TextInput}
                      label="Percentual RCM - pensão por morte (1% a 100%):"
                    />
                  )}
                  <Field
                    name="mesesAposentar"
                    className="mb-3"
                    type="number"
                    component={TextInput}
                    label="Meses para requerimento de benefício na Forluz:"
                  />
                  <Field
                    name="percentualContribuicao"
                    className="mb-3"
                    component={ComboBox}
                    label="Percentual de contribuição:"
                    options={opcoesContribuicao.map((x) => {
                      return { value: x.value, label: x.label };
                    })}
                  />
                </div>
                <div className="col-md-12 col-lg-6 mt-md-4 mt-lg-0">
                  <Field
                    name="remuneracaoMensal"
                    className="mb-3 mb-md-0"
                    label="Remuneração mensal (bruto salarial mais verbas fixas):"
                    component={CurrencyTextInput}
                  />
                  <Field
                    name="crescimentoSalario"
                    className="mb-3"
                    component={ComboBox}
                    label="Aumento real do salário no ano:"
                    options={opcoesCrescimentoSalario.map((x) => {
                      return { value: x.value, label: x.label };
                    })}
                  />
                  {user.plano !== PLANO_TAESA && (
                    <Field
                      name="remuneracoesAno"
                      className="mb-3"
                      component={ComboBox}
                      label="Quantidade de salários de PLR’s no ano. Recomenda-se utilizar o mais conservador:"
                      options={opcoesQtdEstimadaRemuneracao.map((x) => {
                        return { value: x.value, label: x.label };
                      })}
                    />
                  )}

                  <Field
                    name="retornoInvestimento"
                    className="mb-3"
                    component={ComboBox}
                    label="Retorno real de investimento - % ao ano (rentabilidade menos inflação no ano):"
                    options={opcoesRetornoReal.map((x) => {
                      return { value: x.value, label: x.label };
                    })}
                  />
                  <Field
                    name="percentualSaque"
                    component={ComboBox}
                    label="Percentual de parcela à vista, se for o caso:"
                    options={opcoesPercSaque.map((x) => {
                      return { value: x.value, label: x.label };
                    })}
                  />
                </div>
              </div>
              <div className="d-flex mt-32">
                <Button
                  onClick={() => {
                    dispatch(resetaOpcoesTelaRenda());
                    resetForm({
                      values: {
                        tipoRendaMensal: '',
                        percentualRendaMensal: '',
                        mesesAposentar: '',
                        percentualContribuicao: 0,
                        remuneracaoMensal: 0,
                        crescimentoSalario: 0,
                        remuneracoesAno: 0,
                        retornoInvestimento: 0,
                        percentualSaque: 0,
                        prazoRendaTemporaria: 0,
                        percentualrcm: '',
                        percentualSaldoVitalicio: '',
                      },
                    });
                  }}
                  className="md-32"
                  transparent
                  color="orange"
                >
                  Limpar campos
                </Button>
                <Button className="ms-md-32" type="submit" disabled={!isValid}>
                  Continuar
                </Button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};
